import type { Ref } from 'vue';
import type { EventScheduleItemDTO } from '~/shared/api/client/events/events.dto';
import type { MappedPublisherEvent } from '~/shared/api/client/events/events-publisher.dto';
import type { GalleryListItem } from '~/shared/api/client/galleries/galleries.dto';
import { CLIENT_EVENTS_URL } from '~/shared/api/client/events/events.const';
import {
  EVENT_MEDIA_MODES,
  EVENT_VIEW_MODES,
  type EventMediaMode,
  type EventViewMode,
} from '~/shared/lib/events-routes.const';
import type { EventTimeFilter } from './const';
import { EVENT_TIME_FILTERS } from './const';
import {
  OG_SITE_NAME,
  OG_TYPE,
  getAbsoluteUrl,
  getOgImageMeta,
} from '~/shared/lib/seo';
import { useOrigin } from '~/shared/lib/useOrigin';
import { getEventSeoDates } from '~/shared/lib/getEventSeoDates';

const ITEMS_SCRIPT_KEY = 'ld-json-events-items';

type JsonLdEntity = Record<string, unknown>;

function getEventsSeoTitle(
  viewMode: EventViewMode,
  mediaMode: EventMediaMode,
  timeFilter: EventTimeFilter,
): string {
  if (viewMode === EVENT_VIEW_MODES.media) {
    return mediaMode === EVENT_MEDIA_MODES.videos
      ? 'Видеоотчеты мероприятий'
      : 'Фотоотчеты мероприятий';
  }

  if (viewMode === EVENT_VIEW_MODES.schedule) {
    return 'График мероприятий';
  }

  if (timeFilter === EVENT_TIME_FILTERS.past) {
    return 'Прошедшие мероприятия';
  }
  if (timeFilter === EVENT_TIME_FILTERS.upcoming) {
    return 'Предстоящие мероприятия';
  }
  return 'Мероприятия';
}

function getEventsSeoDescription(title: string): string {
  return `${title} CRE: афиша, отчеты, спикеры и расписание событий рынка коммерческой недвижимости.`;
}

function getGalleryCover(item: GalleryListItem): string | null {
  const cover = 'cover_photo' in item ? item.cover_photo : item.cover_video;
  return cover?.file_url ?? null;
}

function useEventsItemListJsonLd(itemListElement: Ref<JsonLdEntity[]>) {
  useHead(() => ({
    script: itemListElement.value.length
      ? [
          {
            type: 'application/ld+json',
            key: ITEMS_SCRIPT_KEY,
            innerHTML: {
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: itemListElement.value.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item,
              })),
            },
          },
        ]
      : [],
  }));
}

export function useEventsSeo(options: {
  viewMode: Ref<EventViewMode>;
  mediaMode: Ref<EventMediaMode>;
  timeFilter: Ref<EventTimeFilter>;
}) {
  const route = useRoute();
  const origin = useOrigin();

  useHead(() => {
    const url = `${origin}${route.fullPath}`;
    const title = getEventsSeoTitle(
      options.viewMode.value,
      options.mediaMode.value,
      options.timeFilter.value,
    );
    const description = getEventsSeoDescription(title);

    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:type', content: OG_TYPE },
        { property: 'og:url', content: url },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:site_name', content: OG_SITE_NAME },
        ...getOgImageMeta(origin),
      ],
      script: [
        {
          type: 'application/ld+json',
          key: 'ld-json-events',
          innerHTML: {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'CollectionPage',
                name: title,
                description,
                url,
                isPartOf: {
                  '@type': 'WebSite',
                  name: OG_SITE_NAME,
                  url: origin,
                },
              },
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Главная',
                    item: origin,
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Мероприятия',
                    item: `${origin}/events`,
                  },
                ],
              },
            ],
          },
        },
      ],
    };
  });
}

export function useEventsAfishaCollectionSeo(
  items: Ref<MappedPublisherEvent[]>,
) {
  const origin = useOrigin();

  useEventsItemListJsonLd(
    computed(() =>
      items.value.map(({ event, imageUrl, place }) => ({
        '@type': 'Event',
        name: event.name,
        description: event.description,
        image: getAbsoluteUrl(origin, imageUrl),
        ...getEventSeoDates(event),
        url: `${origin}${CLIENT_EVENTS_URL}${event.id}`,
        location: place
          ? {
              '@type': 'Place',
              name: place,
              address: place,
            }
          : undefined,
      })),
    ),
  );
}

export function useEventsGalleriesCollectionSeo(options: {
  items: Ref<GalleryListItem[]>;
  mode: Ref<EventMediaMode>;
}) {
  const origin = useOrigin();

  useEventsItemListJsonLd(
    computed(() => {
      const schemaType =
        options.mode.value === EVENT_MEDIA_MODES.videos
          ? 'VideoGallery'
          : 'ImageGallery';
      const detailPath =
        options.mode.value === EVENT_MEDIA_MODES.videos
          ? 'video-gallery'
          : 'photo-gallery';

      return options.items.value.map((item) => ({
        '@type': schemaType,
        name: item.name,
        description: item.description,
        image: getAbsoluteUrl(origin, getGalleryCover(item)),
        datePublished: item.publication_date,
        url: `${origin}/events/${detailPath}/${item.id}`,
      }));
    }),
  );
}

export function useEventsScheduleCollectionSeo(
  events: Ref<EventScheduleItemDTO[]>,
) {
  const origin = useOrigin();

  useEventsItemListJsonLd(
    computed(() =>
      events.value.map((event) => ({
        '@type': 'Event',
        name: event.name,
        description: event.description,
        ...getEventSeoDates(event),
        url: `${origin}${CLIENT_EVENTS_URL}${event.id}`,
      })),
    ),
  );
}
